import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import { buildUserUsageLink } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import UsageScoreBadge from "@/v2-backoffice-client/usage/UsageScoreBadge"
import UsageSparkline from "@/v2-backoffice-client/usage/UsageSparkline"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import {
  formatAppCode,
  formatTrendDetail,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  formatTrialMemberName,
  type TrialCustomer,
  type TrialMember,
} from "@/v2-backoffice-client/license/trials/license.trials.utils"

export type LicenseTrialMembersTableProps = ComponentProps<"div"> & {
  customer: TrialCustomer
}

/** Busiest first; seats with no tracked activity close the list. */
function sortMembers(members: TrialMember[]) {
  return [...members].sort(
    (a, b) =>
      (b.WeightedEvents28d ?? b.Events28d ?? 0) -
        (a.WeightedEvents28d ?? a.Events28d ?? 0) ||
      formatTrialMemberName(a).localeCompare(formatTrialMemberName(b)),
  )
}

// The people behind a trialing customer's row, one line each: who they are
// (and which seated trial product they hold a seat on), how engaged, the
// 28-day shape, active days, trend, the apps they use and when they were
// last seen. A name opens the person's Usage tab. Seats with no activity at
// all are listed too — on a trial, the silence is the finding.
function LicenseTrialMembersTable(props: LicenseTrialMembersTableProps) {
  const { customer, className, ...divProps } = props

  const members = sortMembers(customer.members)

  if (members.length === 0) {
    return (
      <div className={cn("license-trial-members", className)} {...divProps}>
        <p className="text-xs text-muted-foreground">
          Nobody is on this customer yet — no user has been invited or has
          signed up. The trial clock runs regardless.
        </p>
      </div>
    )
  }

  return (
    <div className={cn("license-trial-members", className)} {...divProps}>
      <p className="license-trial-members-title">
        <span>
          <span className="font-medium text-foreground">
            {formatNumber(members.length)}
          </span>{" "}
          {members.length === 1 ? "person" : "people"} on the trial
        </span>
        <span>
          <span className="font-medium text-foreground">
            {formatNumber(customer.summary.activeUsers7d)}
          </span>{" "}
          active the last 7 days
        </span>
        <span>
          <span className="font-medium text-foreground">
            {formatNumber(customer.summary.activeUsers28d)}
          </span>{" "}
          the last 28 days
        </span>
      </p>

      <div className="overflow-x-auto">
        <table className="usage-report-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Score</th>
              <th>Activity · 28 d</th>
              <th
                className="num"
                title="Days with tracked activity in the last 28"
              >
                Active days
              </th>
              <th>Trend</th>
              <th>Apps</th>
              <th>Last active</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => {
              const isActive = (member.Events28d ?? 0) > 0
              return (
                <tr key={member.UserId}>
                  <td>
                    <span className="flex flex-wrap items-center gap-1.5">
                      <Link
                        variant="muted"
                        size="xs"
                        iconSrc={null}
                        to={buildUserUsageLink(member.UserId)}
                        className="font-medium text-foreground underline-offset-2 hover:underline"
                      >
                        {formatTrialMemberName(member)}
                      </Link>
                      {member.seatProducts.map((product) => (
                        <Badge
                          key={product}
                          size="tiny"
                          variant="outline"
                          title={`Holds a named seat on the ${product} trial`}
                        >
                          {product} seat
                        </Badge>
                      ))}
                    </span>
                    {member.Email && (
                      <span className="block max-w-64 truncate text-muted-foreground">
                        {member.Email}
                      </span>
                    )}
                  </td>
                  <td>
                    {isActive ? (
                      <UsageScoreBadge score={member.EngagementScore} compact />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td>
                    {isActive && member.Daily28d ? (
                      <UsageSparkline
                        series={member.Daily28d}
                        className="h-5 w-24"
                      />
                    ) : (
                      <span className="text-muted-foreground">no activity</span>
                    )}
                  </td>
                  <td className="num">
                    {(member.ActiveDays28d ?? 0) > 0 ? (
                      member.ActiveDays28d
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td>
                    {isActive || (member.EventsPrev28d ?? 0) > 0 ? (
                      <UsageTrendBadge
                        trendPct={member.WeightedTrendPct ?? member.TrendPct}
                        detail={formatTrendDetail(
                          member.Events28d ?? 0,
                          member.EventsPrev28d ?? 0,
                        )}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td>
                    {member.AppMix.length > 0 ? (
                      <span className="usage-report-chips">
                        {member.AppMix.map((app) => (
                          <Badge key={app.App} size="tiny" variant="neutral">
                            {formatAppCode(app.App)}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    {member.LastActiveAt ? (
                      <RelativeDate date={member.LastActiveAt} />
                    ) : (
                      <span className="text-muted-foreground">never</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default LicenseTrialMembersTable
